import type { Event } from '@/types/api';
import EventCard from '@/components/common/EventCard';
import { NavigationContext } from '@/types/navigation';
import { useUserProfile } from '@/hooks/use-user';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useEventsRegistrationAvailability } from '@/hooks/use-events-registration-availability';

interface EventListWithContextProps {
    events: Event[];
    lang: string;
    navigationContext: NavigationContext;
    onEventClick?: (event: Event) => void;
}

export default function EventListWithContext({ events, lang, navigationContext, onEventClick }: EventListWithContextProps) {
    const { favoriteEvents, addFavorite, deleteFavorite, favoritesLoading } = useUserProfile();
    const { userProfile } = useUserProfile();
    const { toast } = useToast();
    const { t } = useTranslation();
    // Un único request por lote: sin la ocupación, las tarjetas anuncian como abierto un evento agotado.
    const registrationAvailability = useEventsRegistrationAvailability((events ?? []).map(e => e?.eventId));

    // Function to check if an event is in favorites
    const isEventFavorite = (eventId: number) => {
        if (!favoriteEvents || favoritesLoading) return false;
        return favoriteEvents.some(fav => fav.eventId?.toString() === eventId?.toString());
    };

    // Handler for managing favorites
    const handleFavoriteToggle = async (event: Event, isFavorite: boolean) => {
        // Check if user is authenticated
        if (!userProfile?.id) {
            toast({
                description: t('favorites.toast.login_required'),
                variant: 'destructive',
                duration: 3000
            });
            // Redirect to login with add parameter
            window.open(`/${lang}/login?add=${event.eventId}`, '_blank');
            return false;
        }

        try {
            if (isFavorite) {
                // Remove from favorites
                const success = await deleteFavorite(event.eventId.toString());
                if (success) {
                    toast({
                        description: t('favorites.toast.removed'),
                        variant: 'default',
                        duration: 3000
                    });
                } else {
                    toast({
                        description: t('favorites.toast.remove_error'),
                        variant: 'destructive',
                        duration: 3000
                    });
                }
                return success;
            } else {
                // Add to favorites
                const success = await addFavorite(event.eventId.toString());
                if (success) {
                    toast({
                        description: t('favorites.toast.added'),
                        variant: 'default',
                        duration: 3000
                    });
                } else {
                    toast({
                        description: t('favorites.toast.add_error'),
                        variant: 'destructive',
                        duration: 3000
                    });
                }
                return success;
            }
        } catch (error) {
            console.error('Error managing favorites:', error);
            toast({
                description: t('favorites.toast.manage_error'),
                variant: 'destructive',
                duration: 3000
            });
            return false;
        }
    };

    if (!events || events.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">No se encontraron eventos.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
            {events.map((event) => (
                <EventCard
                    key={event.eventId}
                    event={event}
                    lang={lang}
                    navigationContext={navigationContext}
                    availability={registrationAvailability[event.eventId]}
                    isFavorite={isEventFavorite(event.eventId)}
                    onFavoriteToggle={handleFavoriteToggle}
                    onClick={onEventClick ? () => onEventClick(event) : undefined}
                />
            ))}
        </div>
    );
} 